import {
  BehaviorSubject,
  Observable,
  Subject,
  catchError,
  combineLatest,
  forkJoin,
  map,
  merge,
  of,
  shareReplay,
  switchMap,
  tap,
} from 'rxjs';
import { UserPreferencesService } from '../../common/services/user-preferences/user-preferences.service';
import {
  ALIGN,
  COLUMN_CLICK_TYPE,
  COLUMN_TYPE,
  DropDownOption,
  GridCellData,
  GridColumnSchema,
  GridFilter,
  GridRowData,
  SORT_ORDER,
} from '@maersk-global/angular-shared-library';
import { Screen } from '../../common/api/models/screen';
import { KeyValue } from '../../common/api/models/keyValue';
import { CountryDto } from '../../common/api/models/countryDto';
import { Region } from '../../common/api/models/region';
import { Screens } from '../../common/constants/temporary-constant';
import * as gridconfigurationMapping from './grid-configuration-mapping';
import { SharedDataService } from '../../shared-data-service';
import { SortOrder } from '../../common/api/models/sortOrder';
import { DcrpAuthorizationService } from '../../common/services/authorization/dcrp-authorization.service';
import { AutomaticRecoveryCaseConfigService } from '../../common/api';
import { GetAutomaticRecoveryCaseConfig } from '../../common/api/models/getAutomaticRecoveryCaseConfig';
import { AutomaticRecoveryCaseConfigModel } from '../../common/api/models/automaticRecoveryCaseConfigModel';
import { Column } from '../../common/api/models/column';
import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class AutoInvoiceConfigurationDataManager {
  gridConfigMapping = gridconfigurationMapping.default;
  constructor(
    private _automaticRecoveryCaseConfigService: AutomaticRecoveryCaseConfigService,
    private _userPreferencesService: UserPreferencesService,
    private _sharedDataService: SharedDataService,
    private _dcrpAuthorizationService: DcrpAuthorizationService
  ) {}

  onGridColumnClick?: Function;
  apiVersion: string = '1.0';
  gridFilterPagination: GridFilter = {
    pageNumber: 1,
    pageSize: 10,
    sortColumn: 'recoveryTypeName',
    sortOrder: SORT_ORDER.DESC,
  };

  recoveryTypeOptions: DropDownOption[] = [
    {
      label: 'Low Cost',
      name: '3',
      value: '3',
    },
    {
      label: 'EPR',
      name: '4',
      value: '4',
    },
  ];

  private gridFilterUpdatedSubject$$: Subject<GridFilter> =
    new Subject<GridFilter>();
  gridFilterUpdated$: Observable<GridFilter> =
    this.gridFilterUpdatedSubject$$.asObservable();

  private reloadInitialGrid$$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(true);

  private gridFilterInitial$: Observable<GridFilter> = combineLatest([
    this.reloadInitialGrid$$.asObservable(),
    this._userPreferencesService.autoRecoveryConfigScreen$,
  ]).pipe(
    map(([, screen]) => {
      const gridFilter = { ...this.gridFilterPagination };
      if (
        !screen ||
        !screen.searchCriteria ||
        screen.searchCriteria.length == 0
      )
        return gridFilter;
      screen.searchCriteria.forEach((keyVal: KeyValue) => {
        if (!keyVal.value || !keyVal.key) return;

        if (!gridFilter.filters) gridFilter.filters = {};
        gridFilter.filters[keyVal.key] = JSON.parse(keyVal.value);
      });
      return gridFilter;
    })
  );

  gridFilter$: Observable<GridFilter> = merge(
    this.gridFilterInitial$.pipe(
      tap((filter) => this.fetchDataFromServer(filter))
    ),
    this.gridFilterUpdated$
  );

  private automationConfigFiltersSubject$$: Subject<GetAutomaticRecoveryCaseConfig> =
    new Subject<GetAutomaticRecoveryCaseConfig>();

  automationConfigFilters$: Observable<GetAutomaticRecoveryCaseConfig> =
    this.automationConfigFiltersSubject$$.asObservable();

  gridSchema$: Observable<GridColumnSchema[] | null> = combineLatest([
    this._userPreferencesService.autoRecoveryConfigScreen$,
    this._dcrpAuthorizationService.isAuthorizedFor('automation-config-update'),
  ]).pipe(
    map(([value, isAuthorized]) =>
      this.createAutoRecoveryConfigGridSchema(value, isAuthorized)
    )
  );

  gridDataAndCount$ = this.automationConfigFilters$.pipe(
    switchMap((configRequest) =>
      forkJoin({
        count: this._automaticRecoveryCaseConfigService
          .GetCount(
            {
              ...configRequest,
              paginationPageNumber: undefined,
              paginationSortOrder: undefined,
              paginationPageSize: undefined,
              paginationSortColumn: undefined,
            },
            this.apiVersion
          )
          .pipe(
            map((response) => response as number),
            catchError((error) => {
              // Handle 404 as empty result
              if (error.status === 404) {
                return of(0);
              }
              throw error;
            })
          ),
        configs: this._automaticRecoveryCaseConfigService
          .Get(configRequest, this.apiVersion)
          .pipe(
            map((response) => {
              // Map recoveryTypeName column
              return response as AutomaticRecoveryCaseConfigModel[];
            }),
            catchError((error) => {
              // Handle 404 as empty result
              if (error.status === 404) {
                return of([]);
              }
              throw error;
            })
          ),
      })
    ),
    shareReplay(1)
  );

  gridData$: Observable<GridRowData[] | null> = this.gridDataAndCount$.pipe(
    map((result) =>
      result.configs.length
        ? result.configs.map(this.generateGridDataFromConfig)
        : null
    )
  );

  totalRecords$?: Observable<number> = this.gridDataAndCount$.pipe(
    map((result) => result.count || 0)
  );

  get userPreferences() {
    return this._userPreferencesService.userPreferences;
  }

  get screen() {
    return this.userPreferences?.preference.screens?.find(
      (screen) => screen.id == Screens.AutoRecoveryConfiguration
    );
  }

  reloadGrid() {
    this.reloadInitialGrid$$.next(true);
  }

  fetchDataFromServer(gridFilter: GridFilter) {
    let updatedGridFilter = { ...gridFilter };
    updatedGridFilter = gridFilter.newFiltersApplied
      ? {
          ...this.gridFilterPagination,
          filters: gridFilter.filters,
        }
      : gridFilter;

    this.resetGridPagination(updatedGridFilter);

    // Save filters to server or set the default filters
    //---------------------------------------------
    const configRequest =
      this.convertKeyValToConfigRequestObject(updatedGridFilter);
    this.automationConfigFiltersSubject$$.next(configRequest);
  }

  resetGridPagination(gridFilter: GridFilter) {
    this.gridFilterUpdatedSubject$$.next(gridFilter);
  }

  convertKeyValToConfigRequestObject(gridFilter: GridFilter) {
    var configRequest = {} as GetAutomaticRecoveryCaseConfig;
    if (gridFilter?.filters) {
      Object.keys(gridFilter?.filters)?.forEach((key, _) => {
        const keyValue = gridFilter?.filters ? gridFilter?.filters[key] : '';
        const filterColumDetails = this.gridConfigMapping.filterConfig?.filter(
          (x: any) => x.name == key
        )[0];
        const propertyName =
          filterColumDetails && filterColumDetails.filtername
            ? filterColumDetails.filtername
            : key;
        const propertyValue = keyValue;
        configRequest = {
          ...configRequest,
          [propertyName]: propertyValue ?? null,
        };
      });
    }
    configRequest.paginationPageNumber = gridFilter.pageNumber ?? 1;
    configRequest.paginationPageSize = gridFilter.pageSize ?? 10;
    configRequest.paginationSortColumn =
      gridFilter.sortColumn ?? 'recoveryTypeName';
    configRequest.paginationSortOrder =
      gridFilter.sortOrder == SORT_ORDER.ASC
        ? SortOrder.Ascending
        : SortOrder.Descending;
    return configRequest;
  }

  /**
   *
   * @param value screen specific preferences for the auto recovery config grid.
   */
  createAutoRecoveryConfigGridSchema(
    value: Screen | undefined,
    isAuthorized: boolean
  ) {
    if (!value || !value.columns) return null;
    //Adding edit and delete columns in the grid from UI
    if (value.columns.findIndex((col) => col.name === 'edit') === -1)
      value.columns.push({
        name: 'edit',
        displayName: 'Edit',
        columnType: 'string',
        isHidden: false,
        isSortable: false,
        columnEditType: 'icon',
        icon: 'pencil',
        sequence: 998,
      } as Column);
    if (value.columns.findIndex((col) => col.name === 'delete') === -1)
      value.columns.push({
        name: 'delete',
        displayName: 'Delete',
        columnType: 'string',
        isHidden: false,
        isSortable: false,
        columnEditType: 'icon',
        icon: 'trash',
        sequence: 999,
      } as Column);

    return value.columns.map((column) => {
      if (
        !column.name ||
        !column.displayName ||
        !column.columnType ||
        !column.sequence
      )
        return {} as GridColumnSchema;

      const filterMapping = this.gridConfigMapping.filterConfig.filter(
        (i: { name: any }) => i.name == column.name
      )[0];

      const schema = {
        column: column.name,
        displayName: column.displayName,
        align: column.columnType === 'numeric' ? ALIGN.RIGHT : ALIGN.LEFT,
        hidden: column.isHidden,
        columnType: column.columnType.toUpperCase() as COLUMN_TYPE,
        dateFormat: 'd MMM y h:mm:ss a',
        onClick: this.onGridColumnClick?.bind(this),
        columnClickType: this.selectClickAbleColumnType(column.name),
        sequence: column.sequence,
        isClickable:
          column.name === 'edit' || (column.name === 'delete' && isAuthorized),
        filterType: filterMapping
          ? (filterMapping.type as GridColumnSchema['filterType'])
          : undefined,
        filterSequence: filterMapping ? filterMapping.sequence : 0,
        disableColSorting: !column.isSortable,
        filterDisabled: false,
        loadFilterOptions: () => undefined,
        filterDependsOnColumn: this.getDependentFilterControlName(column.name),
        loadFilterOptionsAsync: (dependentControlValue: unknown) =>
          this.getFilterOptionsByColumnAsync(
            dependentControlValue,
            column.name
          ),
        columnHeaderHidden: ['edit', 'delete'].includes(column.name),
        icon:
          column.name === 'edit'
            ? 'pencil'
            : column.name === 'delete'
              ? 'trash'
              : undefined,
        columnWidth: ['edit', 'delete'].includes(column.name)
          ? { size: 10, unit: 'px' }
          : undefined,
      } as GridColumnSchema;
      return schema;
    });
  }

  /**
   * We are configuring various columns with click type event so that they will be rendered by event type inside grid.
   * @param columnName columnName to configure
   * @returns column click event type
   */
  private selectClickAbleColumnType(
    columnName: string
  ): COLUMN_CLICK_TYPE | undefined {
    switch (columnName) {
      case 'edit':
      case 'delete':
        return COLUMN_CLICK_TYPE.ICON_CLICK;
      default:
        return COLUMN_CLICK_TYPE.LINK_CLICK;
    }
  }

  /**
   * Gets the dependent filter control name based on the provided name.
   *
   * @param name - The name of the filter control.
   * @returns The name of the dependent filter control, or an empty string if there is no dependency.
   */
  private getDependentFilterControlName(name: string): string {
    return name === 'countryName'
      ? 'clusterName'
      : name === 'clusterName'
        ? 'regionName'
        : '';
  }

  /**
   *
   * @param columnName name of the current column
   * @returns Observable
   */
  public getFilterOptionsByColumnAsync(
    dependentControlValue: unknown,
    columnName: string | undefined
  ): Observable<DropDownOption[] | undefined> {
    if (!!dependentControlValue && columnName == 'countryName') {
      return this._sharedDataService.countries$.pipe(
        map((response: CountryDto[] | undefined) => {
          if (!response || response.length < 1) return [];
          return response
            .filter(
              (country) =>
                country.clusterCode &&
                (dependentControlValue as string[]).includes(
                  country.clusterCode
                )
            )
            .map((country) => ({
              value: country.code,
              label: country.name,
              name: country.name,
            }));
        }),
        shareReplay(1)
      );
    } else if (!!dependentControlValue && columnName == 'clusterName') {
      return this._sharedDataService.countries$.pipe(
        map((countries) => {
          if (!countries || countries.length < 1) return [];
          return countries
            .filter(
              (country, index) =>
                country.regionCode &&
                (dependentControlValue as string[]).includes(
                  country.regionCode
                ) &&
                countries.findIndex(
                  (o) => o.clusterCode === country.clusterCode
                ) === index
            )
            .map((cluster) => ({
              value: cluster.clusterCode,
              label: cluster.clusterName,
              name: cluster.clusterName,
            }));
        }),
        shareReplay(1)
      );
    } else if (columnName == 'regionName') {
      return this._sharedDataService.regions$.pipe(
        map((response: Region[] | undefined) => {
          if (!response || response.length < 1) return [];
          return response.map((region) => ({
            value: region.regionCode,
            label: region.regionName,
            name: region.regionName,
          }));
        }),
        shareReplay(1)
      );
    } else if (columnName == 'recoveryTypeName') {
      return of(this.recoveryTypeOptions);
    } else return of(undefined);
  }

  /**
   * Generates grid data from the provided configuration object.
   *
   * @param config - The configuration object of type AutomaticRecoveryCaseConfigModel
   * @returns An object representing a row of grid data, where each key corresponds to a cell in the grid.
   */
  private generateGridDataFromConfig(
    config: AutomaticRecoveryCaseConfigModel
  ): GridRowData {
    const keyValue = config as unknown as {
      [key: string]: unknown;
    };
    const gridRowObject: { [key: string]: GridCellData } = {};
    var keys = Object.keys(config);
    keys.push(...['edit', 'delete']);
    keys.map((key) => {
      gridRowObject[key] = {
        value: Object.keys(config).includes(key) ? keyValue[key] : '',
      } as GridCellData;
    });
    return {
      row: gridRowObject,
    } as GridRowData;
  }
}
